const DEFAULT_OTHER_COLOR = '#A0A9B8';

export const VOLUME_POSITIVE_COLOR = '#7FBF85';
export const VOLUME_NEGATIVE_COLOR = '#D98989';

const defaultPlotColors = [
  '#5CA9E9',
  '#70CA97',
  '#E8A18A',
  DEFAULT_OTHER_COLOR, // historical "Other" slot — still used as fallback when no explicit otherColor is set
  '#AB99D2',
  '#D47E93',
  '#95C7F1',
  '#A2DDBB',
  '#F0C2B3',
  '#C1C7D1',
  '#C8BDE2',
  '#E3ABB9',
  '#4A87BA',
  '#5AA179',
  '#BA816E',
  '#808793',
  '#897AA8',
  '#AA6576',
];

export const newThemePlotColors = [
  '#63A2D9',
  '#7FBF85',
  '#E2D68B',
  '#B493BA',
  '#E2AC8B',
  '#6D73B1',
  '#D38C9E',
  '#977B72',
  '#6BB3AE',
  '#B16D98',
  '#B1705C',
  '#7B8E4B',
  '#84848F',
  '#A9CCEA',
  '#AEDEB2',
  '#EAE4BF',
  '#D8C6DC',
  '#E6C3AD',
  '#AFB3D9',
  '#DCB1BC',
  '#CFB7B0',
  '#B2DCD9',
  '#D1A9C2',
  '#D1A294',
  '#C6D2A7',
  '#C8C8CA',
];

const DEFAULT_OTHER_COLOR_DARK = '#808793';

const defaultDarkPlotColors = [
  '#A9CCEA',
  '#AEDEB2',
  '#EAE4BF',
  '#D8C6DC',
  '#E6C3AD',
  '#AFB3D9',
  '#DCB1BC',
  '#CFB7B0',
  '#B2DCD9',
  '#D1A9C2',
  '#D1A294',
  '#C6D2A7',
  '#C8C8CA',
  '#63A2D9',
  '#7FBF85',
  '#E2D68B',
  '#B493BA',
  '#E2AC8B',
  '#6D73B1',
  '#D38C9E',
  '#977B72',
  '#6BB3AE',
  '#B16D98',
  '#B1705C',
  '#7B8E4B',
  '#84848F',
];

/* Figma v1.1 breakdown ramp — the designer palette frame verbatim (pairs
   light→strong, then grays), plus a small tail for deep breakdowns. */
export const newBreakdownColors = [
  '#a9ccea',
  '#63a2d9',
  '#aedeb2',
  '#7fbf85',
  '#eae4bf',
  '#e2d68b',
  '#d7c8da',
  '#b296b8',
  '#c8c8ca',
  '#84848f',
  '#4a87ba',
  '#5aa179',
];

/**
 * The active chart color palette. Mutated in-place by `setPlotColors`
 * so all existing references see updated values.
 */
export const plotColors: string[] = [...defaultPlotColors];

export const darkPlotColors: string[] = [...defaultDarkPlotColors];

export const breakdownColors: string[] = [...newBreakdownColors];
export const darkBreakdownColors: string[] = [...newBreakdownColors];

/**
 * The active "Other" color, used for generic/residual categories like
 * the "Other" bucket in breakdowns. Kept separate from `plotColors`
 * so it doesn't steal a slot from the ordered palette.
 */
let otherColor: string = DEFAULT_OTHER_COLOR;
let darkOtherColor: string = DEFAULT_OTHER_COLOR_DARK;

const paletteListeners = new Set<() => void>();

export function subscribePaletteChange(listener: () => void): () => void {
  paletteListeners.add(listener);
  return () => {
    paletteListeners.delete(listener);
  };
}

function notifyPaletteChange() {
  for (const listener of paletteListeners) {
    listener();
  }
}

/**
 * Override the chart color palette at runtime (e.g. for whitelabelling).
 * Mutates the existing `plotColors` array in-place so all consumers
 * that import it automatically see the new colors.
 */
export function setPlotColors(colors: string[]) {
  plotColors.length = 0;
  plotColors.push(...colors);
  notifyPaletteChange();
}

/** Reset chart colors back to the Revelio defaults. */
export function resetPlotColors() {
  plotColors.length = 0;
  plotColors.push(...defaultPlotColors);
  notifyPaletteChange();
}

export function setDarkPlotColors(colors: string[]) {
  darkPlotColors.length = 0;
  darkPlotColors.push(...colors);
  notifyPaletteChange();
}

export function resetDarkPlotColors() {
  darkPlotColors.length = 0;
  darkPlotColors.push(...defaultDarkPlotColors);
  notifyPaletteChange();
}

export function setBreakdownColors(colors: string[]) {
  breakdownColors.length = 0;
  breakdownColors.push(...colors);
  notifyPaletteChange();
}

export function resetBreakdownColors() {
  breakdownColors.length = 0;
  breakdownColors.push(...newBreakdownColors);
  notifyPaletteChange();
}

export function setDarkBreakdownColors(colors: string[]) {
  darkBreakdownColors.length = 0;
  darkBreakdownColors.push(...colors);
  notifyPaletteChange();
}

export function resetDarkBreakdownColors() {
  darkBreakdownColors.length = 0;
  darkBreakdownColors.push(...newBreakdownColors);
  notifyPaletteChange();
}

/**
 * Override the "Other" color at runtime (e.g. for whitelabelling).
 * Decouples the generic/residual color from any specific palette index,
 * so ordered categorical bars can use the palette in order.
 */
export function setOtherColor(color: string) {
  otherColor = color;
  notifyPaletteChange();
}

/** Reset the "Other" color back to the Revelio default. */
export function resetOtherColor() {
  otherColor = DEFAULT_OTHER_COLOR;
  notifyPaletteChange();
}

export function setDarkOtherColor(color: string) {
  darkOtherColor = color;
  notifyPaletteChange();
}

export function resetDarkOtherColor() {
  darkOtherColor = DEFAULT_OTHER_COLOR_DARK;
  notifyPaletteChange();
}

export const POSITIVE_COLOR = '#70CA97';
export const NEGATIVE_COLOR = '#D47E93';

/** @deprecated Use `getPrimaryColor()` instead — this is frozen at import time. */
export const PRIMARY_COLOR = '#5CA9E9';

/** @deprecated Use `getOtherColor()` instead — this is frozen at import time. */
export const OTHER_COLOR = DEFAULT_OTHER_COLOR;

/** Returns the current first color in the active palette (updates with whitelabelling). */
export function getPrimaryColor(): string {
  return plotColors[0] ?? '#5CA9E9';
}

export function getPrimaryColorDark(): string {
  return darkPlotColors[0] ?? '#7DBDF0';
}

/** Returns the current "Other" color (updates with whitelabelling). */
export function getOtherColor(): string {
  return otherColor;
}

export function getOtherColorDark(): string {
  return darkOtherColor;
}
